import { useAtom } from "jotai";
import { WebSocketChatResponse } from "../../contexts/useDeepchat";
import emitSnackbar from "../../emitSnackbar";
import { ChatResponseAnswerPartial, ThinkingStepData } from "../../components/Deepchat/models";
import { ChatInteraction, Evidence, MarketResearchStateAtom } from "./models";

export default function useMarketResearchMessageHandler() {
  const [, setState] = useAtom(MarketResearchStateAtom);

  const processSocketMessage = (lastJsonMessage: WebSocketChatResponse<unknown>) => {
    switch (lastJsonMessage?.type) {
      case "chat.thinking_step": {
        const message = lastJsonMessage as WebSocketChatResponse<ThinkingStepData>;

        setState((prev) => ({
          ...prev,
          thinkingSteps: [
            ...prev.thinkingSteps.map((s) => (s.status === "active" ? { ...s, status: "completed" as const } : s)),
            {
              step: message.data.step as any,
              label: message.data.label,
              detail: message.data.detail,
              metadata: message.data.metadata,
              status: "active" as const,
              timestamp: Date.now(),
            },
          ],
        }));
        break;
      }

      case "chat.initial": {
        const message = lastJsonMessage as WebSocketChatResponse<ChatInteraction>;
        // Handle adding responses to the session thread state object
        setState((_state) => {
          const chatInteraction = { ...message.data, complete: false } as ChatInteraction;
          return {
            ..._state,
            messages: [..._state.messages, chatInteraction],
            evidence: [],
          };
        });
        break;
      }

      case "chat.answer": {
        const message = lastJsonMessage as WebSocketChatResponse<ChatResponseAnswerPartial>;

        setState((_state) => {
          const messages = [..._state.messages];
          const lastInteraction = messages.at(-1);

          if (lastInteraction && message) {
            lastInteraction.answer = message.data.answer;
          }

          return {
            ..._state,
            messages,
            isLoading: false,
            // Mark remaining active thinking steps as completed
            thinkingSteps: _state.thinkingSteps.map((s) => ({ ...s, status: "completed" as const })),
          };
        });
        break;
      }

      case "chat.status": {
        const message = lastJsonMessage as WebSocketChatResponse<{ answer: string }>;
        setState((_state) => ({ ..._state, socketStatusMessage: message.data.answer ?? "" }));
        break;
      }

      case "chat.source": {
        const message = lastJsonMessage as WebSocketChatResponse<Evidence>;
        setState((_state) => {
          const messages = [..._state.messages];
          const lastInteraction = messages.at(-1);
          // Append the current evidence messages to the most recent interaction in the message stack
          if (lastInteraction && message) {
            lastInteraction.evidence = [...(lastInteraction.evidence ?? []), message.data];
          }
          return { ..._state, messages };
        });
        break;
      }

      case "chat.complete": {
        setState((_state) => {
          const messages = [..._state.messages];
          const lastInteraction = messages.at(-1);

          if (lastInteraction) {
            // Save the thinking steps into the historical message object
            lastInteraction.thinkingSteps = _state.thinkingSteps.map((s) => ({
              ...s,
              status: s.status === "active" ? ("completed" as const) : s.status,
            }));
            lastInteraction.complete = true;
          }

          return {
            ..._state,
            messages,
            isLoading: false,
            socketStatusMessage: "",
            thinkingSteps: [], // Reset active global steps
          };
        });

        break;
      }

      default: {
        if (
          lastJsonMessage?.type === "chat.exception" ||
          (lastJsonMessage as any)?.message === "Internal server error"
        ) {
          setState((prev) => ({
            ...prev,
            isLoading: false,
            thinkingSteps: prev.thinkingSteps.map((s) =>
              s.status === "active" ? { ...s, status: "failed" as const } : s,
            ),
          }));
          emitSnackbar("Something went wrong, please try again in a moment", "warning");
        }
      }
    }
  };

  return {
    processSocketMessage,
  };
}
